import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import {
  Card,
  CardBody,
  CardHeader,
  FiltersTabs,
  IconButton,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  EmptyState,
} from '../components/ui';
import { AdjustmentsHorizontalIcon, ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';

type StatusFilter =
  | 'all'
  | 'pending'
  | 'submitted'
  | 'processing'
  | 'success'
  | 'failed'
  | 'cancelled'
  | 'expired';

type ManualActionPayload = {
  withdrawalId: number;
  action: 'confirm' | 'reject';
  notes?: string;
};

interface Withdrawal {
  id: number;
  external_trx: string;
  amount: number;
  currency: string;
  payway?: string;
  account: string;
  status: string;
  expires_at?: string;
  callback_url?: string;
  created_at: string;
  updated_at?: string;
  processed_by?: number;
  processed_by_username?: string;
  processed_by_role?: string;
  processed_at?: string;
  processing_notes?: string;
  shop_username?: string;
}

interface WithdrawalDetails {
  withdrawal: Withdrawal;
  logs: any[];
}

const statusOrder: StatusFilter[] = ['all', 'pending', 'submitted', 'processing', 'success', 'failed', 'cancelled', 'expired'];

const statusLabels: Record<StatusFilter, string> = {
  all: 'Все',
  pending: 'Ожидают',
  submitted: 'Отправлены',
  processing: 'В работе',
  success: 'Выполнены',
  failed: 'Отклонены',
  cancelled: 'Отменены',
  expired: 'Просрочены',
};

const withdrawalStatusTone: Record<string, Parameters<typeof StatusBadge>[0]['tone']> = {
  pending: 'warning',
  submitted: 'warning',
  processing: 'info',
  success: 'success',
  failed: 'danger',
  failure: 'danger', // legacy
  cancelled: 'default',
  expired: 'default',
};

const statusText: Record<string, string> = {
  pending: 'Ожидает',
  submitted: 'Отправлен оператору',
  processing: 'В обработке',
  success: 'Выполнен',
  failed: 'Отклонен',
  failure: 'Отклонен', // legacy
  cancelled: 'Отменен',
  expired: 'Истек',
};

const currencySymbols: Record<string, string> = {
  RUB: '₽',
  KZT: '₸',
  USD: '$',
  EUR: '€',
};

const formatCardLike = (value?: string) => {
  if (!value) return '—';
  return value.replace(/\d{12,19}/g, (m) => m.replace(/(\d{4})(?=\d)/g, '$1 ').trim());
};

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const amountWithCurrency = (amount: number, currency: string) =>
  `${amount.toLocaleString('ru-RU')} ${currencySymbols[currency] || currency}`;

const normalizeWithdrawalStatus = (status: string) => (status === 'failure' ? 'failed' : status);

const normalizeWithdrawal = (withdrawal: Withdrawal): Withdrawal => ({
  ...withdrawal,
  status: normalizeWithdrawalStatus(withdrawal.status),
});

const normalizeWithdrawalDetails = (data: WithdrawalDetails): WithdrawalDetails => ({
  ...data,
  withdrawal: normalizeWithdrawal(data.withdrawal),
});

const finalStatuses: Array<Withdrawal['status']> = ['success', 'failed', 'cancelled'];

const isExpired = (withdrawal: Withdrawal): boolean => {
  const status = normalizeWithdrawalStatus(withdrawal.status);
  if (finalStatuses.includes(status)) {
    return false;
  }
  if (withdrawal.expires_at) {
    const expMs = Date.parse(withdrawal.expires_at);
    if (Number.isFinite(expMs)) return Date.now() >= expMs;
  }
  const createdMs = Date.parse(withdrawal.created_at);
  if (!Number.isFinite(createdMs)) return false;
  return Date.now() - createdMs >= 30 * 60 * 1000;
};

const getDisplayStatus = (withdrawal: Withdrawal) => {
  if (isExpired(withdrawal)) {
    return { label: 'Просрочено', tone: 'danger' as Parameters<typeof StatusBadge>[0]['tone'] };
  }
  const status = normalizeWithdrawalStatus(withdrawal.status);
  return {
    label: statusText[status] || status,
    tone: withdrawalStatusTone[status] || 'default',
  };
};

const Withdrawals: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<WithdrawalDetails | null>(null);
  const [processingNotes, setProcessingNotes] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const withdrawalsCountRef = useRef<number | null>(null);
  const location = useLocation();
  const navigateRouter = useNavigate();

  const withdrawalsQuery = useQuery<Withdrawal[]>({
    queryKey: ['withdrawals', user?.role],
    queryFn: async () => {
      const endpoint = user?.role === 'operator' ? '/operator/withdrawals' : '/admin/withdrawals';
      const { data } = await api.get(endpoint);
      return ((data || []) as Withdrawal[]).map(normalizeWithdrawal);
    },
    enabled: !!user && (user.role === 'admin' || user.role === 'operator'),
    refetchInterval: 10_000,
  });

  const withdrawals = withdrawalsQuery.data ?? [];
  const isLoading = withdrawalsQuery.isLoading;

  React.useEffect(() => {
    if (selectedWithdrawal) {
      const { overflow } = document.body.style;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = overflow;
      };
    }
  }, [selectedWithdrawal]);

  useEffect(() => {
    if (withdrawalsCountRef.current !== null && withdrawals.length > (withdrawalsCountRef.current || 0)) {
      try {
        window.dispatchEvent(new Event('notify:new_withdrawal'));
      } catch {}
    }
    withdrawalsCountRef.current = withdrawals.length;
  }, [withdrawals.length]);

  const manualMutation = useMutation<void, unknown, ManualActionPayload>({
    mutationFn: async ({ withdrawalId, action, notes }) => {
      const endpoint = user?.role === 'operator' ? '/operator/withdrawals/manual' : '/admin/withdrawals/manual';
      await api.post(endpoint, { withdrawalId, action, notes });
    },
    onSuccess: () => {
      setProcessingNotes('');
      queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      if (status === 409) {
        alert('Заявка просрочена и не может быть обработана. Список обновлён.');
      } else if (status === 404) {
        alert('Заявка уже обработана или не найдена. Список обновлён.');
      } else {
        alert(`Ошибка: ${error?.response?.data?.error || 'Не удалось выполнить действие'}`);
      }
    },
  });

  const isSubmitting = manualMutation.isPending;

  const loadWithdrawalDetails = async (withdrawalId: number) => {
    try {
      const endpoint = user?.role === 'operator' ? '/operator/withdrawals/' : '/admin/withdrawals/';
      const response = await api.get(`${endpoint}${withdrawalId}`);
      setSelectedWithdrawal(normalizeWithdrawalDetails(response.data));
    } catch (error) {
      console.error('Failed to load withdrawal details:', error);
    }
  };

  const handleManualAction = (withdrawalId: number, action: 'confirm' | 'reject') => {
    const current = withdrawals.find((w) => w.id === withdrawalId);
    if (!current || isExpired(current)) {
      alert('Заявка просрочена и не может быть обработана');
      return;
    }
    manualMutation.mutate({ withdrawalId, action, notes: processingNotes.trim() || undefined });
  };

  useEffect(() => {
    const state = location.state as { initialSearch?: string; withdrawalId?: number } | null;
    if (!state) return;
    if (state.initialSearch) {
      setSearch(state.initialSearch);
      setFilter('all');
    }
    if (state.withdrawalId) {
      loadWithdrawalDetails(state.withdrawalId);
    }
    navigateRouter(location.pathname, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const filteredWithdrawals = useMemo(() => {
    const rawSearch = search.trim();
    const normalizedSearch = rawSearch.toLowerCase();
    const normalizedDigits = normalizedSearch.replace(/\D+/g, '');
    const rawDigits = rawSearch.replace(/\D+/g, '');
    return withdrawals.filter((withdrawal) => {
      const statusNormalized = normalizeWithdrawalStatus(withdrawal.status);
      const matchesFilter =
        filter === 'all' ||
        statusNormalized === filter ||
        (filter === 'expired' && isExpired(withdrawal));
      if (!normalizedSearch) {
        return matchesFilter;
      }

      const searchableText = [
        String(withdrawal.id),
        withdrawal.external_trx ?? '',
        withdrawal.account ?? '',
        withdrawal.payway ?? '',
        statusNormalized ?? '',
        withdrawal.processing_notes ?? '',
        withdrawal.shop_username ?? '',
        withdrawal.processed_by_username ?? '',
        withdrawal.currency ?? '',
      ]
        .filter(Boolean)
        .map((value) => value.toLowerCase());

      const amountValues = [
        withdrawal.amount.toString(),
        withdrawal.amount.toLocaleString('ru-RU'),
        amountWithCurrency(withdrawal.amount, withdrawal.currency),
      ].map((value) => value.toString());

      const matchesText = searchableText.some((value) => value.includes(normalizedSearch));
      const matchesDigits =
        normalizedDigits.length > 0 &&
        [
          withdrawal.account ?? '',
          withdrawal.external_trx ?? '',
          withdrawal.shop_username ?? '',
          withdrawal.processing_notes ?? '',
          withdrawal.processed_by_username ?? '',
        ]
          .filter(Boolean)
          .map((value) => value.replace(/\D+/g, ''))
          .some((value) => value.includes(normalizedDigits));

      const matchesAmount =
        amountValues.some((value) => value.toLowerCase().includes(normalizedSearch)) ||
        (rawSearch ? amountValues.some((value) => value.includes(rawSearch)) : false);
      const matchesAmountDigits =
        rawDigits.length > 0 &&
        amountValues
          .map((value) => value.replace(/\D+/g, ''))
          .some((value) => value.includes(rawDigits));

      return matchesFilter && (matchesText || matchesDigits || matchesAmount || matchesAmountDigits);
    });
  }, [withdrawals, filter, search]);

  const filterTabs = useMemo(() => {
    const counts = withdrawals.reduce<Record<StatusFilter, number>>(
      (acc, withdrawal) => {
        const status = normalizeWithdrawalStatus(withdrawal.status) as StatusFilter;
        if (statusOrder.includes(status)) {
          acc[status] += 1;
        }
        if (isExpired(withdrawal)) {
          acc.expired += 1;
        }
        acc.all += 1;
        return acc;
      },
      {
        all: 0,
        pending: 0,
        submitted: 0,
        processing: 0,
        success: 0,
        failed: 0,
        cancelled: 0,
        expired: 0,
      }
    );
    return statusOrder.map((key) => ({ key, label: statusLabels[key], badge: counts[key] }));
  }, [withdrawals]);

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-slate-900">Управление выводами</h1>
          <p className="text-sm text-slate-500">Контролируйте очередь заявок и оперативно реагируйте на проблемные кейсы.</p>
        </div>
        <div className="flex items-center gap-2">
          <IconButton onClick={() => withdrawalsQuery.refetch()} aria-label="Обновить список" variant="ghost">
            <ArrowPathIcon className="h-5 w-5" />
          </IconButton>
          <div className="hidden items-center gap-2 rounded-full border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-1.5 shadow-sm sm:flex">
            <AdjustmentsHorizontalIcon className="h-5 w-5 text-[var(--app-text-muted)]" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Поиск по ID, трекеру, реквизитам"
              className="w-64 border-none bg-transparent text-sm text-[var(--app-text)] outline-none placeholder:text-[var(--app-text-muted)]"
            />
          </div>
        </div>
      </div>

      <Card>
        <CardHeader
          className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"
          title={<span className="text-lg font-semibold text-slate-900">Очередь заявок</span>}
          description="Фильтруйте заявки по статусу и оперативно подтверждайте или отклоняйте их."
          actions={
            <FiltersTabs
              className="hidden md:inline-flex"
              tabs={filterTabs}
              value={filter}
              onChange={(key) => setFilter(key as StatusFilter)}
            />
          }
        />
        <CardBody className="space-y-4">
          <div className="flex flex-col gap-3 rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] p-4 shadow-sm transition-colors sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-[var(--app-heading)]">Комментарий к ручной обработке</p>
              <p className="text-xs text-[var(--app-text-muted)]">Он будет записан к заявке при подтверждении/отклонении.</p>
            </div>
            <textarea
              value={processingNotes}
              onChange={(event) => setProcessingNotes(event.target.value)}
              placeholder="Например: проверено по телефону, реквизиты подтверждены"
              rows={2}
              className="w-full rounded-xl border border-[var(--app-border)] bg-[var(--app-input-bg)] p-2 text-sm text-[var(--app-text)] focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 sm:max-w-md"
            />
          </div>

          <div className="flex items-center gap-2 rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-2 shadow-sm sm:hidden">
            <AdjustmentsHorizontalIcon className="h-5 w-5 text-[var(--app-text-muted)]" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Поиск по выводам"
              className="w-full border-none bg-transparent text-sm text-[var(--app-text)] outline-none placeholder:text-[var(--app-text-muted)]"
            />
          </div>

          <div className="space-y-1 md:hidden">
            <label className="text-xs font-semibold uppercase text-secondary-500">Статус</label>
            <select
              value={filter}
              onChange={(event) => setFilter(event.target.value as StatusFilter)}
              className="w-full rounded-full border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            >
              {filterTabs.map((tab) => (
                <option key={tab.key} value={tab.key}>
                  {tab.label}
                  {tab.badge !== undefined ? ` (${tab.badge})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="grid gap-3 md:hidden">
            {isLoading ? (
              <div className="rounded-2xl border border-[var(--app-border)] px-4 py-6 text-center text-[var(--app-text-muted)]">
                Загружаем заявки...
              </div>
            ) : filteredWithdrawals.length === 0 ? (
              <EmptyState
                title="Заявки не найдены"
                description="Измените фильтр или обновите список."
              />
            ) : (
              filteredWithdrawals.map((withdrawal) => (
                <div
                  key={withdrawal.id}
                  className="space-y-3 rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] p-4 shadow-sm transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-[var(--app-heading)]">#{withdrawal.id}</p>
                      <p className="text-xs text-[var(--app-text-muted)]">Трекер: {withdrawal.external_trx || '—'}</p>
                    </div>
                    <div className="space-y-1 text-right">
                      {(() => {
                        const display = getDisplayStatus(withdrawal);
                        return <StatusBadge tone={display.tone}>{display.label}</StatusBadge>;
                      })()}
                    </div>
                  </div>
                  <div className="space-y-1 text-sm text-[var(--app-text)]">
                    <p className="text-base font-semibold leading-tight tracking-tight text-[var(--app-heading)] whitespace-nowrap">
                      {amountWithCurrency(withdrawal.amount, withdrawal.currency)}
                    </p>
                    {withdrawal.payway ? (
                      <p className="text-xs text-[var(--app-text-muted)]">Канал: {withdrawal.payway}</p>
                    ) : null}
                    <p className="text-xs text-[var(--app-text-muted)]">
                      Реквизиты: {formatCardLike(withdrawal.account)}
                    </p>
                    <p className="text-xs text-[var(--app-text-muted)]">
                      Создан: {formatDateTime(withdrawal.created_at)}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {withdrawal.status === 'pending' ? (
                      <>
                        <button
                          onClick={() => handleManualAction(withdrawal.id, 'confirm')}
                          className="rounded-full border border-success/40 bg-success/10 px-3 py-1 text-xs font-semibold text-success transition hover:bg-success/20"
                          disabled={isSubmitting}
                        >
                          Подтвердить
                        </button>
                        <button
                          onClick={() => handleManualAction(withdrawal.id, 'reject')}
                          className="rounded-full border border-danger/40 bg-danger/10 px-3 py-1 text-xs font-semibold text-danger transition hover:bg-danger/20"
                          disabled={isSubmitting}
                        >
                          Отклонить
                        </button>
                      </>
                    ) : null}
                    <button
                      onClick={() => loadWithdrawalDetails(withdrawal.id)}
                      className="rounded-full border border-[var(--app-border)] px-3 py-1 text-xs font-semibold text-primary-600 transition hover:border-primary-200"
                    >
                      Детали
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="hidden md:block">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>ID / Трекер</TableHeaderCell>
                    <TableHeaderCell>Сумма</TableHeaderCell>
                    <TableHeaderCell>Реквизиты</TableHeaderCell>
                    <TableHeaderCell>Статус</TableHeaderCell>
                    <TableHeaderCell>Создан</TableHeaderCell>
                    <TableHeaderCell>Обработан</TableHeaderCell>
                    <TableHeaderCell numeric>Действия</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-slate-500">
                        Загружаем заявки...
                      </TableCell>
                    </TableRow>
                  ) : filteredWithdrawals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-slate-500">
                        Заявок с таким фильтром не найдено.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredWithdrawals.map((withdrawal) => (
                      <TableRow key={withdrawal.id}>
                        <TableCell>
                          <div className="space-y-1 text-sm font-semibold text-slate-900">
                            <div>#{withdrawal.id}</div>
                            <div className="text-xs text-slate-500">Трекер: {withdrawal.external_trx || '—'}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1 text-sm text-slate-600">
                            <div className="text-base font-semibold leading-tight tracking-tight text-slate-900 whitespace-nowrap">
                              {amountWithCurrency(withdrawal.amount, withdrawal.currency)}
                            </div>
                            {withdrawal.payway ? (
                              <div className="text-xs text-slate-500">Платёжный канал: {withdrawal.payway}</div>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1 text-sm text-slate-600">
                            <div className="font-medium text-slate-800">{formatCardLike(withdrawal.account)}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const display = getDisplayStatus(withdrawal);
                            return <StatusBadge tone={display.tone}>{display.label}</StatusBadge>;
                          })()}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-slate-600">{formatDateTime(withdrawal.created_at)}</div>
                          {withdrawal.expires_at ? (
                            <div className="text-xs text-slate-400">Истекает: {formatDateTime(withdrawal.expires_at)}</div>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {withdrawal.processed_by_username ? (
                            <div className="space-y-1 text-sm text-slate-600">
                              <div>{withdrawal.processed_by_username}</div>
                              {withdrawal.processed_at ? (
                                <div className="text-xs text-slate-500">{formatDateTime(withdrawal.processed_at)}</div>
                              ) : null}
                            </div>
                          ) : (
                            <span className="text-sm text-slate-400">—</span>
                          )}
                        </TableCell>
                        <TableCell numeric>
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => loadWithdrawalDetails(withdrawal.id)}
                              className="text-sm font-semibold text-primary-600 transition hover:text-primary-700"
                            >
                              Детали
                            </button>
                            {(user?.role === 'admin' || user?.role === 'operator') && withdrawal.status === 'pending' ? (
                              !isExpired(withdrawal) ? (
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => handleManualAction(withdrawal.id, 'confirm')}
                                    className="rounded-full bg-success/10 px-2.5 py-1 text-xs font-semibold text-success transition hover:bg-success/20"
                                    disabled={isSubmitting}
                                  >
                                    Подтвердить
                                  </button>
                                  <button
                                    onClick={() => handleManualAction(withdrawal.id, 'reject')}
                                    className="rounded-full bg-danger/10 px-2.5 py-1 text-xs font-semibold text-danger transition hover:bg-danger/20"
                                    disabled={isSubmitting}
                                  >
                                    Отклонить
                                  </button>
                                </div>
                              ) : (
                                <span className="rounded-full bg-surface-subtle px-3 py-1 text-xs text-slate-400">
                                  Просрочено
                                </span>
                              )
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </CardBody>
      </Card>

      {selectedWithdrawal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 sm:px-6">
          <div
            className="absolute inset-0 bg-[var(--app-bg)]/70 backdrop-blur-sm transition"
            onClick={() => setSelectedWithdrawal(null)}
          />
          <div className="relative z-10 w-full max-w-xl overflow-hidden rounded-3xl border border-[var(--app-border)] bg-[var(--app-surface)] shadow-lifted">
            <div className="flex items-center justify-between border-b border-[var(--app-border)] px-5 py-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-secondary-500">Детали вывода</p>
                <h2 className="text-base font-semibold text-[var(--app-heading)]">#{selectedWithdrawal.withdrawal.id}</h2>
              </div>
              <IconButton aria-label="Закрыть" onClick={() => setSelectedWithdrawal(null)}>
                <XMarkIcon className="h-5 w-5" />
              </IconButton>
            </div>
            <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto px-5 py-5 text-sm">
              <Card className="shadow-none border border-[var(--app-border)] bg-[var(--app-surface-subtle)]/70">
                <CardBody className="space-y-3 text-sm text-[var(--app-text)]">
                  <div>
                    <p className="text-[11px] font-semibold uppercase text-secondary-500">Реквизиты</p>
                    <p className="text-sm font-semibold text-[var(--app-heading)]">
                      {formatCardLike(selectedWithdrawal.withdrawal.account)}
                    </p>
                    {selectedWithdrawal.withdrawal.payway ? (
                      <p className="text-xs text-[var(--app-text-muted)]">Канал: {selectedWithdrawal.withdrawal.payway}</p>
                    ) : null}
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase text-secondary-500">Трекер</p>
                    <p className="text-sm text-[var(--app-text)]">{selectedWithdrawal.withdrawal.external_trx || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase text-secondary-500">Сумма</p>
                    <p className="text-base font-semibold leading-tight tracking-tight text-[var(--app-heading)]">
                      {amountWithCurrency(selectedWithdrawal.withdrawal.amount, selectedWithdrawal.withdrawal.currency)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-semibold uppercase text-secondary-500">Статус</p>
                    {(() => {
                      const display = getDisplayStatus(selectedWithdrawal.withdrawal);
                      return <StatusBadge tone={display.tone}>{display.label}</StatusBadge>;
                    })()}
                  </div>
                  <div className="grid grid-cols-1 gap-3 text-xs text-[var(--app-text-muted)]">
                    <div>
                      <p className="text-[11px] font-semibold uppercase text-secondary-500">Создан</p>
                      <p className="text-[var(--app-text)]">{formatDateTime(selectedWithdrawal.withdrawal.created_at)}</p>
                    </div>
                    {selectedWithdrawal.withdrawal.expires_at ? (
                      <div>
                        <p className="text-[11px] font-semibold uppercase text-secondary-500">Истекает</p>
                        <p className="text-[var(--app-text)]">{formatDateTime(selectedWithdrawal.withdrawal.expires_at)}</p>
                      </div>
                    ) : null}
                    {selectedWithdrawal.withdrawal.processed_by_username ? (
                      <div>
                        <p className="text-[11px] font-semibold uppercase text-secondary-500">Обработал</p>
                        <p className="text-[var(--app-text)]">{selectedWithdrawal.withdrawal.processed_by_username}</p>
                        {selectedWithdrawal.withdrawal.processed_at ? (
                          <p className="text-xs text-[var(--app-text-muted)]">
                            {formatDateTime(selectedWithdrawal.withdrawal.processed_at)}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                    {selectedWithdrawal.withdrawal.processing_notes ? (
                      <div>
                        <p className="text-[11px] font-semibold uppercase text-secondary-500">Примечания</p>
                        <p className="text-[var(--app-text)]">{selectedWithdrawal.withdrawal.processing_notes}</p>
                      </div>
                    ) : null}
                  </div>
                </CardBody>
              </Card>

              {selectedWithdrawal.logs.length ? (
                <Card className="shadow-none border border-[var(--app-border)] bg-[var(--app-surface-subtle)]/70">
                  <CardHeader
                    title={<span className="text-[var(--app-heading)]">История событий</span>}
                    description="События аудита по данной заявке."
                  />
                  <CardBody className="max-h-48 space-y-2 overflow-y-auto text-xs">
                    {selectedWithdrawal.logs.map((log, index) => (
                      <div
                        key={index}
                        className="rounded-xl border border-[var(--app-border)] bg-[var(--app-surface)]/80 p-3 text-sm text-[var(--app-text)]"
                      >
                        <p className="font-semibold text-[var(--app-heading)]">{log.action}</p>
                        <p className="text-xs text-[var(--app-text-muted)]">
                          {formatDateTime(log.ts)} · {log.actor}
                        </p>
                      </div>
                    ))}
                  </CardBody>
                </Card>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Withdrawals;
